import { Models } from '@luxuryescapes/lib-flights-types'
import { journeyV2Map } from 'api/lib/journeyV2Map'
import { EmployeeTripRequestItem, EmployeeTripRequestsResponseResult } from 'businessTraveller/types/tripRequestTypes'

export function isHotelLikeTripRequestItem(item: EmployeeTripRequestItem): item is App.BusinessTraveller.AccommodationTripRequestItem {
  return item.type === 'accommodation' || item.type === 'bedbank'
}

export function mapTripRequestItemsToHotelItems(
  tripRequestId: string,
  tripRequestItems: Array<App.BusinessTraveller.AccommodationTripRequestItem>,
  employees: Array<App.BusinessTraveller.EmployeeFromMeEndpoint>,
): Array<App.BusinessTraveller.HotelItem> {
  return tripRequestItems.map((item, index) => ({
    id: `${tripRequestId}-${index}`,
    name: item.displayData.name || '',
    detailName: item.displayData.detailName || '',
    location: item.displayData.location || '',
    image: item.displayData.imageId || '',
    checkIn: item.displayData.checkIn || '',
    checkOut: item.displayData.checkOut || '',
    duration: item.displayData.duration || 0,
    travellers: mapTripRequestItemTravellers(item.tripRequestItemTravellers, employees),
  }))
}

type TripRequestItemTraveller = App.BusinessTraveller.TripRequestItemTraveller

/**
 * Maps trip request item travellers to a simplified traveller format.
 * 3 roles: Employee | Named Guest | Pure Guest
 */
export function mapTripRequestItemTravellers(
  tripRequestItemTravellers: Array<TripRequestItemTraveller> | undefined,
  employees: Array<App.BusinessTraveller.EmployeeFromMeEndpoint>,
): Array<{ firstName: string; lastName: string; employeeId?: string }> {
  return (tripRequestItemTravellers ?? []).map(traveller => {
    if (traveller.employeeId) {
      const employee = employees.find(e => e.id === traveller.employeeId)
      return {
        firstName: employee?.firstName ?? '',
        lastName: employee?.lastName ?? '',
        employeeId: traveller.employeeId,
      }
    }
    return {
      firstName: traveller.guestTravellerDetails?.firstName,
      lastName: traveller.guestTravellerDetails?.lastName,
    }
  })
}

export function mapTripRequest(
  request: EmployeeTripRequestsResponseResult,
  tripRequestItem: EmployeeTripRequestsResponseResult['tripRequestItems'][number],
  employees: Array<App.BusinessTraveller.EmployeeFromMeEndpoint>,
  hotelItems?: Array<App.BusinessTraveller.HotelItem>,
  tripRequestEmployees?: Array<App.BusinessTraveller.EmployeeFromMeEndpoint>,
): App.BusinessTraveller.ApprovalRequest {
  const requester = employees.find((employee) => employee.id === request.requesterId)
  const mappedApprovalRequest: App.BusinessTraveller.ApprovalRequest = {
    ...request,
    tripRequestItems: request.tripRequestItems as Array<App.BusinessTraveller.TripRequestItem>,
    details: { flightItems: tripRequestItem.type === 'flight' ? (tripRequestItem.details?.flightItems as Array<Models.Search.Fare>).map(item => journeyV2Map(item)) : undefined },
    requesterName: requester ? `${requester.firstName} ${requester.lastName}` : '',
    hotelItems, // @deprecated Use tripRequestItems instead.
    type: tripRequestItem.type === 'flight' ? 'flight' : tripRequestItem.type === 'car_hire' ? 'car_hire' : 'hotel',
    employees: tripRequestEmployees,
  }
  return mappedApprovalRequest
}

export function mapApprovalRequest(
  request: EmployeeTripRequestsResponseResult,
  employees: Array<App.BusinessTraveller.EmployeeFromMeEndpoint>,
  hotelItems?: Array<App.BusinessTraveller.HotelItem>,
): App.BusinessTraveller.ApprovalRequest {
  const requester = employees.find((employee) => employee.id === request.requesterId)
  const employeeIds = request.employeeIds ?? []
  const tripRequestEmployees = employees.filter(e => employeeIds.includes(e.id))
  const mappedApprovalRequest: App.BusinessTraveller.ApprovalRequest = {
    ...request,
    tripRequestItems: request.tripRequestItems as Array<App.BusinessTraveller.TripRequestItem>,
    details: { flightItems: request.type === 'flight' ? (request.details?.flightItems as Array<Models.Search.Fare>).map(item => journeyV2Map(item)) : undefined },
    requesterName: requester ? `${requester.firstName} ${requester.lastName}` : '',
    hotelItems, // @deprecated Use tripRequestItems instead.
    employees: tripRequestEmployees,
  }
  return mappedApprovalRequest
}

/**
 * Builds a deduplicated map of travellers from trip request items.
 * For employee travellers with missing guest details, falls back to the employee state.
 * Guests without a guestId are skipped.
 */
export function buildTripRequestTravellerMap(
  tripRequest: App.BusinessTraveller.ApprovalRequest | undefined,
  employees: { [employeeId: string]: App.BusinessTraveller.EmployeeFromMeEndpoint } | undefined,
): Map<string, App.BusinessTraveller.GuestTravellerDetails> {
  const travellerMap = new Map<string, App.BusinessTraveller.GuestTravellerDetails>()

  tripRequest?.tripRequestItems?.forEach((item) => {
    item.tripRequestItemTravellers?.forEach((traveller) => {
      const { employeeId, guestTravellerDetails } = traveller

      // In the new inline trip request form, we store both guest id and guest info in the guest traveller details object
      const key = employeeId || guestTravellerDetails.guestId

      // If no key means this traveller is a guest and did not have guest details stored, skip
      if (!key) {
        return
      }

      // Append seat selections to existing traveller
      const existing = travellerMap.get(key)
      if (existing) {
        if (guestTravellerDetails.seatSelections?.length) {
          travellerMap.set(key, {
            ...existing,
            seatSelections: [
              ...(existing.seatSelections ?? []),
              ...guestTravellerDetails.seatSelections,
            ],
          })
        }
        return
      }

      // For employee travellers, fall back to the employee state if guestTravellerDetails is missing required details
      const hasRequiredGuestDetails = guestTravellerDetails.firstName && guestTravellerDetails.lastName && guestTravellerDetails.email
      if (!hasRequiredGuestDetails) {
        if (employeeId) {
          const employee = employees?.[employeeId]
          travellerMap.set(key, {
            ...guestTravellerDetails,
            // Show incomplete guest details when employee is not found in state (deleted employee)
            firstName: employee?.firstName ?? guestTravellerDetails.firstName ?? '',
            lastName: employee?.lastName ?? guestTravellerDetails.lastName ?? '',
            email: guestTravellerDetails?.email || employee?.email || '',
          })
        }
        return
      }

      // For travellers with complete guest details, set the guest details in the map
      travellerMap.set(key, guestTravellerDetails)
    })
  })

  return travellerMap
}

export function constructFlightUrl(approvalRequest: App.BusinessTraveller.ApprovalRequest) {
  if (approvalRequest.status !== 'APPROVED') {
    return ''
  }

  const flightItems = approvalRequest.details?.flightItems || []
  const travellerCount = (approvalRequest.guestCount ?? 0) + (approvalRequest.employeeIds?.length ?? 0) || 1
  const firstFlight = flightItems[0]?.flightGroup?.flights[0]

  if (!flightItems.length || !firstFlight) {
    return ''
  }

  if (flightItems[0]?.fareType === 'MultiCity') {
    const params: any = {
      adults: travellerCount,
      children: 0,
      infants: 0,
      childrenAge: '',
      fareClass: 'economy',
      fareType: 'multiCity',
      approvalRequest: approvalRequest.id,
    }

    // Add flight segments
    flightItems.forEach((item, index) => {
      const flight = item.flightGroup.flights[0]
      params[`flights[${index}][originAirportCode]`] = flight.departureAirport
      params[`flights[${index}][originAirportName]`] = flight.departureAirportName
      params[`flights[${index}][destinationAirportCode]`] = flight.arrivalAirport
      params[`flights[${index}][destinationAirportName]`] = flight.arrivalAirportName
      params[`flights[${index}][departDate]`] = flight.departingDate
    })

    return `/flights-search-results-multi-city?${new URLSearchParams(params).toString()}`
  }

  // else create URL for non-multi-city flights
  const lastFlight = flightItems.at(-1)?.flightGroup.flights[0]
  const lastFlightOfFirstGroup = flightItems[0].flightGroup.flights.at(-1)

  const flightSearchParams = new URLSearchParams({
    originAirportCode: firstFlight.departureAirport,
    originAirportName: firstFlight.departureAirportName,
    destinationAirportCode: lastFlightOfFirstGroup?.arrivalAirport || '',
    destinationAirportName: lastFlightOfFirstGroup?.arrivalAirportName || '',
    departDate: firstFlight.departingDate,
    returnDate: lastFlight?.departingDate || '',
    adults: travellerCount.toString(),
    children: '0',
    infants: '0',
    fareClass: 'economy',
    fareType: flightItems.length > 1 ? 'return' : 'oneWay',
    approvalRequest: approvalRequest.id,
  })

  return `/flights-search-results?${flightSearchParams.toString()}`
}

export function getCartRestoreUrl(tripRequest: App.BusinessTraveller.ApprovalRequest) {
  return `/checkout-restore-cart?cartId=${tripRequest.cartId}&approvalRequestId=${tripRequest.id}`
}

function getDefaultCarHireSearchType(locationId: string): 'airport' | 'destination' {
  return locationId.length === 3 ? 'airport' : 'destination'
}

export function constructCarHireUrl(approvalRequest: App.BusinessTraveller.ApprovalRequest): string {
  if (approvalRequest.status !== 'APPROVED') return ''
  const carHireItem = approvalRequest.tripRequestItems?.find(
    (item): item is App.BusinessTraveller.CarHireTripRequestItem => item.type === 'car_hire',
  )
  if (!carHireItem?.displayData) return getCartRestoreUrl(approvalRequest)
  const { pickUpLocation, dropOffLocation, pickupDate, dropoffDate } = carHireItem.displayData
  const details = carHireItem.displayData.carHireDetails
  if (!details?.pickUpId || !details?.dropOffId || !pickupDate || !dropoffDate || !details?.pickUpTime || !details?.dropOffTime) {
    return getCartRestoreUrl(approvalRequest)
  }
  const pickUpSearchType = details.pickUpSearchType ?? getDefaultCarHireSearchType(details.pickUpId)
  const dropOffSearchType = details.dropOffSearchType ?? getDefaultCarHireSearchType(details.dropOffId)
  const params: Record<string, string> = {
    pickUpName: pickUpLocation ?? '',
    dropOffName: dropOffLocation ?? '',
    pickUpId: details.pickUpId,
    dropOffId: details.dropOffId,
    pickUpDate: pickupDate,
    dropOffDate: dropoffDate,
    pickUpTime: details.pickUpTime,
    dropOffTime: details.dropOffTime,
    pickUpSearchType,
    dropOffSearchType,
    approvalRequest: approvalRequest.id,
  }
  if (details.ageCategory != null) params.ageCategory = String(details.ageCategory)
  else params.ageCategory = '2' // default: 30-69 age band for API compatibility
  if (details.age != null) params.age = String(details.age)
  if (details.offerId) params.approvedOfferId = details.offerId
  // Do not add insurance to URL: when insurance=enhanced, offers without insuranceUpsell are
  // filtered out client-side, hiding under-budget options. The approved car's insurance is
  // applied at checkout from approval data (insuranceFromBackend in onGoToCheckout).
  return `/search/car-hire?${new URLSearchParams(params).toString()}`
}

export function proceedWithBookingUrl(tripRequest: App.BusinessTraveller.ApprovalRequest): string | undefined {
  if (tripRequest) {
    switch (tripRequest.type) {
      case 'flight':
        return constructFlightUrl(tripRequest)
      case 'hotel':
        return getCartRestoreUrl(tripRequest)
      case 'car_hire':
        return constructCarHireUrl(tripRequest)
      default:
        return undefined
    }
  }
  return undefined
}
